import {
  localProductionEnabled,
  localProductionOnly,
} from '../lib/localProduction'
import { ploteaApp, qaPanel } from '../lib/productProfile'
import { surroundingsPreviewUrl } from '../lib/surroundingsPreview'
import { siteEnabled } from '../lib/site'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../lib/theme'
import { useRouterState } from '@tanstack/react-router'
import {
  KeyRound,
  Activity,
  Building2,
  ChartNoAxesCombined,
  FileSearch,
  FlaskConical,
  LayoutGrid,
  LibraryBig,
  ListChecks,
  BriefcaseBusiness,
  Database,
  LandPlot,
  Map,
  Moon,
  PackageSearch,
  Server,
  Settings,
  Sun,
  Tag,
  Users,
} from 'lucide-react'
import { TESTID } from '../lib/testids'
import { useAdminOperationsAlert } from '../hooks/useAdminOperationsAlert'
import { ArchigmaLogo, ArchigmaMark, PloteaLogo, PloteaMark } from './Logo'
import { NavMain, type AppNavItem } from './nav-main'
import { NavUser } from './nav-user'
import { OrgSwitcher } from './OrgSwitcher'
import { SurroundingsNavItem } from './SurroundingsNavItem'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from './ui/sidebar'

const PLATFORM_NAV: readonly AppNavItem[] = [
  ...(localProductionEnabled
    ? [{ label: 'Produkcja', to: '/production' as const, icon: Activity }]
    : []),
  {
    label: 'Jobs',
    to: '/jobs',
    icon: LayoutGrid,
    exact: true,
    testId: TESTID.navJobs,
  },
  ...(import.meta.env.VITE_BUILDING_REVIEW_ENABLED === 'true'
    ? [
        {
          label: 'Modele budynków',
          to: '/building-review' as const,
          icon: PackageSearch,
        },
      ]
    : []),
  { label: 'Catalog', to: '/catalog', icon: LibraryBig },
  { label: 'Asset Hub', to: '/asset-hub', icon: PackageSearch },
  ...(surroundingsPreviewUrl
    ? [{ label: 'Surroundings', to: '/surroundings' as const, icon: Map }]
    : []),
]

const SITE_NAV: readonly AppNavItem[] = [
  { label: 'Analiza', to: '/site', icon: LandPlot, exact: true },
  { label: 'Portfolio', to: '/site/portfolio', icon: BriefcaseBusiness },
  { label: 'Przesiew działek', to: '/site/screening', icon: ListChecks },
  { label: 'Źródła danych', to: '/site/sources', icon: Database },
]

const ADMIN_NAV: readonly AppNavItem[] = [
  {
    label: 'Machines',
    to: '/admin/machines',
    icon: Server,
    testId: TESTID.navAdminMachines,
  },
  {
    label: 'CI/CD & DR',
    to: '/admin/operations',
    icon: Activity,
    testId: TESTID.navAdminOperations,
  },
  {
    label: 'Code Analysis',
    to: '/admin/code-analysis',
    icon: FileSearch,
    testId: TESTID.navAdminCodeAnalysis,
  },
  {
    label: 'Tests',
    to: '/admin/tests',
    icon: FlaskConical,
  },
  {
    label: 'Users',
    to: '/admin/users',
    icon: Users,
    testId: TESTID.navAdminUsers,
  },
  {
    label: 'Organizacje',
    to: '/admin/organizations',
    icon: Building2,
    testId: TESTID.navAdminOrganizations,
  },
  { label: 'Versions', to: '/admin/versions', icon: Tag },
  { label: 'Zadania w tle', to: '/admin/jobs', icon: ListChecks },
  {
    label: 'External Keys',
    to: '/admin/external-api-keys',
    icon: KeyRound,
  },
  {
    label: 'Scheduler',
    to: '/admin/scheduler-settings',
    icon: Settings,
  },
]

const PRODUCT_ANALYTICS_NAV: readonly AppNavItem[] = [
  {
    label: 'Product analytics',
    to: '/admin/product-analytics',
    icon: ChartNoAxesCombined,
    testId: TESTID.navProductAnalytics,
  },
]

export function AppSidebar({
  userName,
  isSystemAdmin,
  isProductAnalyticsReader,
  onSettings,
  onLogout,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  userName: string
  isSystemAdmin: boolean
  isProductAnalyticsReader: boolean
  onSettings?: () => void
  onLogout: () => void
}) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const { state, isMobile } = useSidebar()
  const brandVariant = state === 'collapsed' && !isMobile ? 'icon' : 'expanded'
  const operationsAlert = useAdminOperationsAlert(
    isSystemAdmin && !qaPanel && !ploteaApp,
  )
  const operationsAlertMessage =
    operationsAlert.data?.level === 'critical'
      ? (operationsAlert.data.message ?? 'Krytyczny problem CI/CD lub DR')
      : operationsAlert.isError
        ? 'Nie udało się pobrać stanu CI/CD i DR'
        : undefined

  return (
    <Sidebar collapsible="icon" data-testid={TESTID.sidebar} {...props}>
      <SidebarHeader className="gap-0 border-b border-sidebar-border p-2">
        <SidebarBrand variant={brandVariant} />
        {brandVariant === 'expanded' && !localProductionOnly && <OrgSwitcher />}
      </SidebarHeader>

      <SidebarContent>
        {/* The Plotea app carries Site only (docs/architecture/plotea.md). */}
        {!ploteaApp && (
          <NavMain
            label="Platform"
            items={
              localProductionOnly
                ? PLATFORM_NAV.filter((item) => item.to === '/production')
                : qaPanel
                  ? PLATFORM_NAV.filter(
                      (item) => item.to === '/building-review',
                    )
                  : PLATFORM_NAV
            }
            pathname={pathname}
            customItems={
              surroundingsPreviewUrl && !qaPanel
                ? {
                    '/surroundings': (
                      <SurroundingsNavItem
                        key="surroundings"
                        pathname={pathname}
                      />
                    ),
                  }
                : undefined
            }
          />
        )}
        {siteEnabled && !qaPanel && !localProductionOnly && (
          <NavMain label="Site" items={SITE_NAV} pathname={pathname} />
        )}
        {isSystemAdmin && !qaPanel && !ploteaApp && (
          <NavMain
            label="System administration"
            items={ADMIN_NAV}
            pathname={pathname}
            alerts={
              operationsAlertMessage
                ? { '/admin/operations': operationsAlertMessage }
                : undefined
            }
          />
        )}
        {isProductAnalyticsReader && !qaPanel && !ploteaApp && (
          <NavMain
            label="Internal analytics"
            items={PRODUCT_ANALYTICS_NAV}
            pathname={pathname}
          />
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarThemeToggle />
        {!localProductionOnly && (
          <NavUser
            userName={userName}
            onSettings={onSettings}
            onLogout={onLogout}
          />
        )}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

function SidebarThemeToggle() {
  const { dark, toggle } = useTheme()
  const { t } = useTranslation()
  const label = dark ? t('settings.theme.light') : t('settings.theme.dark')
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton onClick={toggle} aria-label={label} tooltip={label}>
          {dark ? <Sun /> : <Moon />}
          <span>{label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export function SidebarBrand({ variant }: { variant: 'expanded' | 'icon' }) {
  if (ploteaApp) {
    return variant === 'icon' ? (
      <div className="flex h-11 w-full items-center justify-center">
        <PloteaMark className="size-5 shrink-0" />
      </div>
    ) : (
      <div className="flex min-h-11 items-center px-2">
        <div className="min-w-0">
          <PloteaLogo className="h-auto w-16" />
          <div className="mt-1 font-mono text-[0.58rem] font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Analiza działek
          </div>
        </div>
      </div>
    )
  }
  if (variant === 'icon') {
    return (
      <div className="flex h-11 w-full items-center justify-center">
        <ArchigmaMark className="h-5 w-3.5 shrink-0 text-sidebar-foreground" />
      </div>
    )
  }

  return (
    <div className="flex min-h-11 items-center px-2">
      <div className="min-w-0">
        <ArchigmaLogo className="h-auto w-36" />
        <div className="mt-1 font-mono text-[0.58rem] font-medium uppercase tracking-[0.12em] text-muted-foreground">
          {localProductionOnly
            ? 'Produkcja / lokalnie'
            : qaPanel
              ? 'Kontrola modeli'
              : 'Agents for architecture / 01'}
        </div>
      </div>
    </div>
  )
}
